import { useEffect, useState } from 'react';

// Short replies still read as written, long ones don't keep the HM waiting.
const MIN_MS = 250;
const MAX_MS = 800;
const MS_PER_CHAR = 3;

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * How much of a reply shows `elapsed` ms into its reveal. Eased out, so most of it is in early, and cut at the end of
 * a word, so Streamdown parses the Markdown once per word rather than every frame.
 */
export const revealedLength = (text: string, elapsed: number) => {
  const duration = Math.min(
    MAX_MS,
    Math.max(MIN_MS, text.length * MS_PER_CHAR),
  );
  const progress = Math.min(1, elapsed / duration);
  const cut = Math.ceil(text.length * (1 - (1 - progress) ** 3));
  const wordEnd = text.slice(cut).search(/\s/);
  return wordEnd === -1 ? text.length : cut + wordEnd;
};

/**
 * Emulated streaming: a reply that arrives while the chat is open appears word by word, since the API returns it
 * whole. Streamdown renders the half-written Markdown cleanly. Shown at once with reduced motion.
 */
export const useRevealText = (text: string, reveal: boolean) => {
  const [animating] = useState(() => reveal && !prefersReducedMotion());
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (!animating) return;
    const start = performance.now();
    let frame = requestAnimationFrame(function step(now) {
      const length = revealedLength(text, now - start);
      setShown(length);
      if (length < text.length) frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [animating, text]);

  return animating ? text.slice(0, shown) : text;
};
